import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ViewChild, ElementRef, NgZone,
  ViewEncapsulation, Renderer2
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';

function passwordStartsWithM(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  if (value && value[0].toLowerCase() !== 'm') {
    return { startsWithM: true };
  }
  return null;
}
import { Router } from '@angular/router';
import { LoginEstudianteFacadeService } from './login-estudiante-facade.service';

interface Dot {
  x: number; y: number;
  vx: number; vy: number;
  r: number;
}

@Component({
  selector: 'app-login-estudiante',
  templateUrl: './login-estudiante.component.html',
  styleUrls: ['./login-estudiante.component.css'],
  encapsulation: ViewEncapsulation.Emulated
})
export class LoginEstudianteComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('leCanvas') private canvasRef!: ElementRef<HTMLCanvasElement>;

  public formLogin: FormGroup;
  public hide = true;
  public year: number = new Date().getFullYear();
  public usuarioFirstCharWarning = false;

  private animId = 0;
  private _warnTimer: ReturnType<typeof setTimeout> | null = null;
  private mx = -9999;
  private my = -9999;
  private dots: Dot[] = [];
  private resizeFn = () => this.resizeCanvas();
  private mouseFn = (e: MouseEvent) => { this.mx = e.clientX; this.my = e.clientY; };

  constructor(
    public router: Router,
    public loginFacade: LoginEstudianteFacadeService,
    private _renderer: Renderer2,
    private ngZone: NgZone
  ) {
    this.formLogin = new FormGroup({
      usuario:  new FormControl('', [Validators.required, passwordStartsWithM]),
      password: new FormControl('', [Validators.required])
    });
  }

  ngOnInit(): void {
    this._renderer.addClass(document.body, 'login-student-dark');
    document.documentElement.style.backgroundColor = '#0f1923';
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => this.startCanvas());
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animId);
    window.removeEventListener('resize', this.resizeFn);
    document.removeEventListener('mousemove', this.mouseFn);
    this._renderer.removeClass(document.body, 'login-student-dark');
    document.documentElement.style.backgroundColor = '';
    if (this._warnTimer) clearTimeout(this._warnTimer);
  }

  private startCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;

    this.resizeCanvas();
    window.addEventListener('resize', this.resizeFn);
    document.addEventListener('mousemove', this.mouseFn);

    const MAX_DIST = 145;
    const MOUSE_R  = 120;

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const d of this.dots) {
        const dx = d.x - this.mx;
        const dy = d.y - this.my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < MOUSE_R && dist > 0) {
          const f = ((MOUSE_R - dist) / MOUSE_R) * 0.60;
          d.vx += (dx / dist) * f;
          d.vy += (dy / dist) * f;
        }

        const spd = Math.sqrt(d.vx * d.vx + d.vy * d.vy);
        if (spd > 2.5) { d.vx *= 2.5 / spd; d.vy *= 2.5 / spd; }
        d.vx *= 0.976;
        d.vy *= 0.976;

        if (Math.abs(d.vx) < 0.12) d.vx += (Math.random() - 0.5) * 0.055;
        if (Math.abs(d.vy) < 0.12) d.vy += (Math.random() - 0.5) * 0.055;

        d.x += d.vx;
        d.y += d.vy;

        if (d.x < 0) d.x = canvas.width;
        else if (d.x > canvas.width) d.x = 0;
        if (d.y < 0) d.y = canvas.height;
        else if (d.y > canvas.height) d.y = 0;
      }

      // Líneas de conexión
      for (let i = 0; i < this.dots.length; i++) {
        for (let j = i + 1; j < this.dots.length; j++) {
          const a = this.dots[i], b = this.dots[j];
          const ddx = a.x - b.x, ddy = a.y - b.y;
          const d = Math.sqrt(ddx * ddx + ddy * ddy);
          if (d < MAX_DIST) {
            const alpha = (1 - d / MAX_DIST) * 0.28;
            ctx.strokeStyle = `rgba(200,235,255,${alpha})`;
            ctx.lineWidth = 0.9;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Puntos
      for (const d of this.dots) {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(210,238,255,0.85)';
        ctx.fill();
      }

      this.animId = requestAnimationFrame(tick);
    };

    tick();
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const n = Math.min(Math.floor((canvas.width * canvas.height) / 11000), 100);
    this.dots = Array.from({ length: n }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r:  Math.random() * 1.6 + 0.7
    }));
  }

  login() {
    if (this.formLogin.invalid) {
      this.formLogin.markAllAsTouched();
      return;
    }
    this.loginFacade.loginEstudiante(this.formLogin.value);
  }

  volver() {
    this.router.navigateByUrl('/inicio');
  }

  allowAlphanumeric(event: KeyboardEvent) {
    const key = event.key;
    if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) return;
    if (!/^[a-zA-Z0-9]$/.test(key)) event.preventDefault();
  }

  onUsuarioKeydown(event: KeyboardEvent): void {
    const controlKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Shift', 'Control', 'Alt', 'CapsLock', 'Meta', 'Enter'];
    if (controlKeys.includes(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;

    const input = event.target as HTMLInputElement;
    if ((input.selectionStart ?? 0) === 0 && event.key.toLowerCase() !== 'm') {
      event.preventDefault();
      this._showUsuarioWarning();
    }
  }

  onUsuarioInput(): void {
    const value = (this.formLogin.get('usuario')?.value as string) ?? '';
    if (value.length > 0 && value[0].toLowerCase() !== 'm') {
      this._showUsuarioWarning();
    } else {
      this._clearUsuarioWarning();
    }
  }

  private _showUsuarioWarning(): void {
    this.usuarioFirstCharWarning = true;
    if (this._warnTimer) clearTimeout(this._warnTimer);
    this._warnTimer = setTimeout(() => {
      this.usuarioFirstCharWarning = false;
      this._warnTimer = null;
    }, 3000);
  }

  private _clearUsuarioWarning(): void {
    this.usuarioFirstCharWarning = false;
    if (this._warnTimer) {
      clearTimeout(this._warnTimer);
      this._warnTimer = null;
    }
  }
}
